/* Reusable Appligator voice assistant panel. */
(function () {
  const { useEffect, useRef, useState } = React;
  const Icon = window.Icon;

  const DEFAULT_VOICE_ASSISTANT_INSTRUCTIONS = "";

  function voiceAssistantContext(db, view, viewLabel, pageContext) {
    const currentScreenDraft = pageContext && pageContext.kind === "screenDraft" && pageContext.draftName
      ? [
          `The user is currently looking at the screen draft "${pageContext.draftName}".`,
          pageContext.projectName ? `Project: ${pageContext.projectName}` : ""
        ].filter(Boolean).join("\n")
      : "";
    if (currentScreenDraft) return currentScreenDraft;
    return viewLabel || view ? `Current view: ${viewLabel || view}` : "";
  }

  function normalizeChatMessages(messages) {
    return (Array.isArray(messages) ? messages : []).map((message, index) => ({
      id: message.id || `${message.role || message.Role || "message"}-${index}`,
      role: message.role || message.Role || "assistant",
      content: message.content || message.Content || message.text || "",
      createdAt: message.createdAt || message.CreatedAt || new Date().toISOString(),
      inputMode: message.inputMode || message.InputMode || "Text",
      streaming: false,
    })).filter((message) => message.content);
  }

  function cleanSpeechText(value) {
    return String(value || "")
      .replace(/```[\s\S]*?```/g, " ")
      .replace(/[`*_#[\]()]/g, "")
      .replace(/\s+/g, " ")
      .trim();
  }

  function VoiceAssistantButton({ open, onToggle }) {
    return (
      <button
        type="button"
        className={`voice-trigger ${open ? "active" : ""}`}
        title={open ? "Close voice assistant" : "Open voice assistant"}
        aria-label={open ? "Close voice assistant" : "Open voice assistant"}
        aria-expanded={open}
        data-appligator-id="voice-assistant-open"
        onClick={onToggle}
      >
        <Icon name="mic" size={16} />
      </button>
    );
  }

  function VoiceAssistantPanel({
    db,
    view,
    viewLabel,
    context,
    pageContext,
    instructions = DEFAULT_VOICE_ASSISTANT_INSTRUCTIONS,
    buildContext = voiceAssistantContext,
    onClose,
    onError
  }) {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const canRealtime = !!(window.RTCPeerConnection && navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
    const canDictate = !!SpeechRecognition;
    const canListen = canRealtime || canDictate;
    const canSpeak = !!(window.speechSynthesis && window.SpeechSynthesisUtterance);
    const [threadId, setThreadId] = useState("");
    const [messages, setMessages] = useState([]);
    const [draft, setDraft] = useState("");
    const [listening, setListening] = useState(false);
    const [busy, setBusy] = useState(false);
    const [status, setStatus] = useState(canListen ? "Ready" : "Typed fallback");
    const [error, setError] = useState("");
    const [autoSpeak, setAutoSpeak] = useState(canSpeak);
    const recognitionRef = useRef(null);
    const realtimeRef = useRef(null);
    const realtimeAssistantTextRef = useRef("");
    const realtimeAssistantRecordedRef = useRef(false);
    const pendingTranscriptRef = useRef("");
    const unsubscribeRef = useRef(null);
    const lastEventSequenceRef = useRef(0);
    const threadRef = useRef("");
    const conversationVersionRef = useRef(0);
    const toolNamesByKeyRef = useRef({});
    const processedRealtimeToolCallsRef = useRef({});
    const messagesEndRef = useRef(null);

    useEffect(() => { threadRef.current = threadId; }, [threadId]);
    useEffect(() => () => {
      if (unsubscribeRef.current) unsubscribeRef.current();
      if (recognitionRef.current) recognitionRef.current.abort();
      stopRealtimeVoice(false);
      if (window.speechSynthesis) window.speechSynthesis.cancel();
    }, []);
    useEffect(() => {
      if (messagesEndRef.current) messagesEndRef.current.scrollIntoView({ block: "end" });
    }, [messages.length, busy]);

    function currentContext() {
      if (typeof context === "string") return context;
      return typeof buildContext === "function" ? buildContext(db, view, viewLabel, pageContext) : "";
    }

    function currentInstructions() {
      return String(instructions || "").trim();
    }

    function setPanelError(nextError) {
      const message = (nextError && nextError.message) || nextError || "Voice assistant failed.";
      setError(message);
      setStatus("Blocked");
      if (onError) onError(new Error(message));
    }

    function speak(text) {
      const clean = cleanSpeechText(text);
      if (!autoSpeak || !canSpeak || !clean) return;
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(clean);
      utterance.rate = 1.02;
      utterance.pitch = 1;
      window.speechSynthesis.speak(utterance);
    }

    function mergeAssistantDelta(delta) {
      if (!delta) return;
      setMessages((current) => {
        const next = current.slice();
        const last = next[next.length - 1];
        if (last && last.role === "assistant" && last.streaming) {
          next[next.length - 1] = { ...last, content: `${last.content || ""}${delta}` };
        } else {
          next.push({ id: `assistant-${Date.now()}`, role: "assistant", content: delta, createdAt: new Date().toISOString(), inputMode: "Text", streaming: true });
        }
        return next;
      });
    }

    function completeAssistant(text, speakReply = true) {
      let spoken = text || "";
      setMessages((current) => {
        const next = current.slice();
        const last = next[next.length - 1];
        if (last && last.role === "assistant" && last.streaming) {
          spoken = text || last.content || "";
          next[next.length - 1] = { ...last, content: spoken, streaming: false };
          return next;
        }
        if (spoken) next.push({ id: `assistant-${Date.now()}`, role: "assistant", content: spoken, createdAt: new Date().toISOString(), inputMode: "Text", streaming: false });
        return next;
      });
      if (spoken && speakReply) speak(spoken);
    }

    function realtimeToolKey(event, toolName) {
      return String((event && (event.item_id || event.call_id || event.output_index || event.response_id)) || toolName || "tool");
    }

    function toolNameFromEvent(event) {
      if (!event) return "";
      const item = event.item || event.output_item || {};
      const itemType = String(item.type || event.item_type || event.type || "").toLowerCase();
      const name = event.toolName || event.tool_name || event.name || item.name || (item.function && item.function.name) || "";
      if (name) return String(name).trim();
      return itemType.includes("function_call") || itemType.includes("tool") ? "tool" : "";
    }

    function setToolActivity(toolName, toolKey, state = "running") {
      const cleanName = String(toolName || "").trim();
      if (!cleanName) return;
      const key = String(toolKey || cleanName);
      toolNamesByKeyRef.current[key] = cleanName;
      setMessages((current) => {
        const next = current.slice();
        const existingIndex = next.findIndex((message) => message.kind === "tool" && message.toolKey === key);
        const item = {
          id: `tool-${key}`,
          kind: "tool",
          role: "assistant",
          toolName: cleanName,
          toolKey: key,
          content: cleanName,
          createdAt: new Date().toISOString(),
          inputMode: "Text",
          streaming: state === "running",
        };
        if (existingIndex >= 0) {
          next[existingIndex] = { ...next[existingIndex], ...item, createdAt: next[existingIndex].createdAt };
          return next;
        }
        next.push(item);
        return next;
      });
    }

    function finishToolActivity(toolName, toolKey) {
      const key = String(toolKey || toolName || "");
      const cleanName = String(toolName || (key && toolNamesByKeyRef.current[key]) || "").trim();
      if (!cleanName) return;
      setToolActivity(cleanName, key || cleanName, "done");
    }

    function finishRunningToolActivities() {
      setMessages((current) => current.map((message) => message.kind === "tool" && message.streaming
        ? { ...message, streaming: false }
        : message));
    }

    function restartConversation() {
      conversationVersionRef.current += 1;
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
      if (recognitionRef.current) {
        recognitionRef.current.abort();
        recognitionRef.current = null;
      }
      stopRealtimeVoice(false);
      if (window.speechSynthesis) window.speechSynthesis.cancel();
      threadRef.current = "";
      lastEventSequenceRef.current = 0;
      pendingTranscriptRef.current = "";
      realtimeAssistantTextRef.current = "";
      realtimeAssistantRecordedRef.current = false;
      toolNamesByKeyRef.current = {};
      processedRealtimeToolCallsRef.current = {};
      setThreadId("");
      setMessages([]);
      setDraft("");
      setBusy(false);
      setListening(false);
      setError("");
      setStatus("Ready");
    }

    function subscribeToThread(nextThreadId, afterSequence, conversationVersion = conversationVersionRef.current) {
      if (unsubscribeRef.current) unsubscribeRef.current();
      unsubscribeRef.current = window.API.subscribeChatGptThread(nextThreadId, {
        onEvent: (item) => {
          if (conversationVersion !== conversationVersionRef.current) return;
          lastEventSequenceRef.current = Math.max(lastEventSequenceRef.current, Number(item.sequence || item.Sequence) || 0);
          const type = item.type || item.Type;
          const data = item.data || item.Data || {};
          if (type === "response.started") {
            setBusy(true);
            setError("");
            setStatus("Thinking");
          }
          if (type === "response.chunk") {
            mergeAssistantDelta(data.delta || data.Delta || "");
          }
          if (type === "response.completed") {
            setBusy(false);
            setStatus("Ready");
            finishRunningToolActivities();
            completeAssistant(data.text || data.Text || "");
          }
          if (type === "response.tool_call") {
            const toolName = data.toolName || data.ToolName || "tool";
            const toolKey = data.toolCallId || data.ToolCallId || toolName;
            const state = String(data.status || data.Status || "running").toLowerCase();
            if (state === "done" || state === "completed") finishToolActivity(toolName, toolKey);
            else setToolActivity(toolName, toolKey, "running");
          }
          if (type === "error") {
            setBusy(false);
            finishRunningToolActivities();
            setPanelError(data.error || data.Error || "OpenAI response failed.");
          }
        },
        onError: () => {
          if (conversationVersion !== conversationVersionRef.current) return;
          setBusy(false);
          setPanelError("The assistant stream disconnected.");
        }
      }, afterSequence || 0);
    }

    async function sendPrompt(prompt, mode = "Voice") {
      const clean = String(prompt || "").trim();
      if (!clean || busy || listening) return;
      if (!window.API.startChatGptThread || !window.API.sendChatGptThreadMessage || !window.API.subscribeChatGptThread) {
        setPanelError("Voice assistant API client is not available.");
        return;
      }
      setBusy(true);
      setError("");
      setStatus("Sending");
      setDraft("");
      window.speechSynthesis && window.speechSynthesis.cancel();
      setMessages((current) => [...current, { id: `user-${Date.now()}`, role: "user", content: clean, createdAt: new Date().toISOString(), inputMode: mode, streaming: false }]);
      const conversationVersion = conversationVersionRef.current;
      try {
        const body = {
          prompt: clean,
          instructions: currentInstructions(),
          context: currentContext(),
          inputMode: mode,
        };
        const afterSequence = lastEventSequenceRef.current;
        const response = threadRef.current
          ? await window.API.sendChatGptThreadMessage(threadRef.current, body)
          : await window.API.startChatGptThread(body);
        if (conversationVersion !== conversationVersionRef.current) return;
        const nextThreadId = response && (response.threadId || response.ThreadId);
        if (nextThreadId && !threadRef.current) {
          threadRef.current = nextThreadId;
          setThreadId(nextThreadId);
        }
        const returnedMessages = normalizeChatMessages(response && (response.messages || response.Messages));
        if (!messages.length && returnedMessages.length > 1) setMessages(returnedMessages);
        subscribeToThread(nextThreadId || threadRef.current, afterSequence, conversationVersion);
      } catch (err) {
        if (conversationVersion !== conversationVersionRef.current) return;
        setBusy(false);
        setPanelError(err);
      }
    }

    function appendRealtimeMessage(role, content, inputMode = "Voice", streaming = false) {
      const clean = String(content || "").trim();
      if (!clean) return;
      setMessages((current) => [...current, { id: `${role}-${Date.now()}`, role, content: clean, createdAt: new Date().toISOString(), inputMode, streaming }]);
    }

    async function recordRealtimeMessage(role, content) {
      const clean = String(content || "").trim();
      const currentThreadId = threadRef.current;
      if (!clean || !currentThreadId || !window.API.recordChatGptRealtimeMessage) return;
      const conversationVersion = conversationVersionRef.current;
      try {
        const response = await window.API.recordChatGptRealtimeMessage(currentThreadId, { role, content: clean, inputMode: "Voice" });
        if (conversationVersion !== conversationVersionRef.current) return;
        const returnedMessages = normalizeChatMessages(response && (response.messages || response.Messages));
        if (returnedMessages.length) setMessages(returnedMessages);
      } catch (err) {
        if (conversationVersion !== conversationVersionRef.current) return;
        setPanelError(err);
      }
    }

    function realtimeFunctionCallFromEvent(event) {
      const item = (event && (event.item || event.output_item)) || {};
      const itemType = String(item.type || event.item_type || "").toLowerCase();
      const toolName = toolNameFromEvent(event);
      const callId = String(event.call_id || item.call_id || event.item_id || item.id || "").trim();
      if (!toolName || toolName === "tool" || !callId) return null;
      if (event.type !== "response.function_call_arguments.done" && itemType !== "function_call") return null;
      return {
        toolName,
        callId,
        toolKey: realtimeToolKey(event, toolName),
        argumentsText: event.arguments || item.arguments || (item.function && item.function.arguments) || "{}",
      };
    }

    function parseRealtimeToolArguments(argumentsText) {
      try {
        const parsed = JSON.parse(argumentsText || "{}");
        return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
      } catch (_) {
        return {};
      }
    }

    async function executeRealtimeToolCall(call) {
      if (!call || processedRealtimeToolCallsRef.current[call.callId]) return;
      processedRealtimeToolCallsRef.current[call.callId] = true;
      setToolActivity(call.toolName, call.toolKey, "running");
      try {
        if (!window.API.executeChatGptTool) throw new Error("Voice assistant tool API client is not available.");
        const current = realtimeRef.current;
        const channel = current && current.channel;
        if (!channel || channel.readyState !== "open") throw new Error("Realtime voice channel is not connected.");
        const result = await window.API.executeChatGptTool({
          toolName: call.toolName,
          arguments: parseRealtimeToolArguments(call.argumentsText),
        });
        channel.send(JSON.stringify({
          type: "conversation.item.create",
          item: {
            type: "function_call_output",
            call_id: call.callId,
            output: JSON.stringify(result || {}),
          },
        }));
        channel.send(JSON.stringify({ type: "response.create" }));
        finishToolActivity(call.toolName, call.toolKey);
      } catch (err) {
        finishToolActivity(call.toolName, call.toolKey);
        setPanelError(err);
      }
    }

    async function handleRealtimeEvent(event) {
      const type = event && event.type;
      if (!type) return;
      if (type === "response.output_item.added" || type === "conversation.item.created") {
        const toolName = toolNameFromEvent(event);
        if (toolName) {
          setToolActivity(toolName, realtimeToolKey(event, toolName), "running");
          return;
        }
      }
      if (type === "response.output_item.done" || type === "response.function_call_arguments.done") {
        const call = realtimeFunctionCallFromEvent(event);
        if (call) {
          await executeRealtimeToolCall(call);
          return;
        }
        const toolName = toolNameFromEvent(event);
        const toolKey = realtimeToolKey(event, toolName);
        finishToolActivity(toolName, toolKey);
        return;
      }
      if (type === "conversation.item.input_audio_transcription.completed") {
        const transcript = event.transcript || "";
        appendRealtimeMessage("user", transcript, "Voice");
        recordRealtimeMessage("user", transcript);
        return;
      }
      if (type === "response.output_audio_transcript.delta" || type === "response.output_text.delta") {
        const delta = event.delta || "";
        if (!delta) return;
        realtimeAssistantTextRef.current += delta;
        realtimeAssistantRecordedRef.current = false;
        mergeAssistantDelta(delta);
        return;
      }
      if (type === "response.output_audio_transcript.done" || type === "response.output_text.done") {
        const text = event.transcript || event.text || realtimeAssistantTextRef.current || "";
        if (text.trim()) {
          completeAssistant(text, false);
          if (!realtimeAssistantRecordedRef.current) {
            realtimeAssistantRecordedRef.current = true;
            recordRealtimeMessage("assistant", text);
          }
        }
        realtimeAssistantTextRef.current = "";
        return;
      }
      if (type === "response.done" && realtimeAssistantTextRef.current.trim() && !realtimeAssistantRecordedRef.current) {
        const text = realtimeAssistantTextRef.current;
        completeAssistant(text, false);
        finishRunningToolActivities();
        realtimeAssistantRecordedRef.current = true;
        realtimeAssistantTextRef.current = "";
        recordRealtimeMessage("assistant", text);
        return;
      }
      if (type === "response.done") {
        finishRunningToolActivities();
        return;
      }
      if (type === "error") {
        finishRunningToolActivities();
        setPanelError((event.error && event.error.message) || "OpenAI realtime response failed.");
      }
    }

    async function startRealtimeVoice() {
      if (!canRealtime || busy || listening) return false;
      if (!window.API.startChatGptRealtimeSession || !window.API.recordChatGptRealtimeMessage) {
        setPanelError("Realtime voice API client is not available.");
        return false;
      }
      setBusy(true);
      setError("");
      setStatus("Connecting");
      window.speechSynthesis && window.speechSynthesis.cancel();
      const conversationVersion = conversationVersionRef.current;
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        if (conversationVersion !== conversationVersionRef.current) {
          stream.getTracks().forEach((track) => track.stop());
          return false;
        }
        const pc = new RTCPeerConnection();
        const audio = new Audio();
        audio.autoplay = true;
        pc.ontrack = (event) => {
          audio.srcObject = event.streams[0];
        };
        stream.getTracks().forEach((track) => pc.addTrack(track, stream));
        const channel = pc.createDataChannel("oai-events");
        channel.onmessage = (message) => {
          if (conversationVersion !== conversationVersionRef.current) return;
          try {
            handleRealtimeEvent(JSON.parse(message.data || "{}")).catch((err) => setPanelError(err));
          } catch (err) {
            setPanelError(err);
          }
        };
        channel.onerror = () => {
          if (conversationVersion === conversationVersionRef.current) setPanelError("Realtime voice channel failed.");
        };
        channel.onclose = () => {
          if (conversationVersion !== conversationVersionRef.current) return;
          setListening(false);
          setStatus("Ready");
        };

        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        const localSdp = (pc.localDescription && pc.localDescription.sdp) || offer.sdp || "";
        if (!localSdp.trim().startsWith("v=0")) {
          throw new Error("Browser did not create a valid realtime voice offer.");
        }
        const response = await window.API.startChatGptRealtimeSession({
          threadId: threadRef.current,
          instructions: currentInstructions(),
          context: currentContext(),
        });
        if (conversationVersion !== conversationVersionRef.current) {
          pc.close();
          stream.getTracks().forEach((track) => track.stop());
          return false;
        }
        const clientSecret = response && (response.clientSecret || response.ClientSecret);
        if (!clientSecret) {
          throw new Error("Realtime voice session did not include a client secret.");
        }
        const nextThreadId = response && (response.threadId || response.ThreadId);
        if (nextThreadId) {
          threadRef.current = nextThreadId;
          setThreadId(nextThreadId);
        }
        const returnedMessages = normalizeChatMessages(response && (response.messages || response.Messages));
        if (returnedMessages.length) setMessages(returnedMessages);
        const sdpResponse = await fetch("https://api.openai.com/v1/realtime/calls", {
          method: "POST",
          headers: {
            "Accept": "application/sdp",
            "Authorization": `Bearer ${clientSecret}`,
            "Content-Type": "application/sdp",
          },
          body: localSdp,
        });
        const answerSdp = await sdpResponse.text();
        if (conversationVersion !== conversationVersionRef.current) {
          pc.close();
          stream.getTracks().forEach((track) => track.stop());
          return false;
        }
        if (!sdpResponse.ok) {
          let message = answerSdp || sdpResponse.statusText || "Realtime voice connection failed.";
          try {
            const errorBody = JSON.parse(answerSdp || "{}");
            message = (errorBody.error && errorBody.error.message) || message;
          } catch (_) {}
          throw new Error(`OpenAI realtime request failed with ${sdpResponse.status}: ${message}`);
        }
        await pc.setRemoteDescription({ type: "answer", sdp: answerSdp });
        realtimeRef.current = { pc, channel, stream, audio };
        realtimeAssistantTextRef.current = "";
        realtimeAssistantRecordedRef.current = false;
        setListening(true);
        setStatus("Live voice");
        return true;
      } catch (err) {
        if (conversationVersion !== conversationVersionRef.current) return false;
        setPanelError(err);
        stopRealtimeVoice(false);
        return false;
      } finally {
        if (conversationVersion === conversationVersionRef.current) setBusy(false);
      }
    }

    function stopRealtimeVoice(updateStatus = true) {
      const current = realtimeRef.current;
      realtimeRef.current = null;
      if (current) {
        try { current.channel && current.channel.close(); } catch (_) {}
        try { current.pc && current.pc.close(); } catch (_) {}
        try { current.stream && current.stream.getTracks().forEach((track) => track.stop()); } catch (_) {}
        try { current.audio && (current.audio.srcObject = null); } catch (_) {}
      }
      setListening(false);
      if (updateStatus) setStatus("Ready");
    }

    function startDictation() {
      if (!canDictate || busy) return;
      setError("");
      pendingTranscriptRef.current = "";
      const recognition = new SpeechRecognition();
      recognition.lang = navigator.language || "en-US";
      recognition.continuous = false;
      recognition.interimResults = true;
      recognition.onstart = () => {
        setListening(true);
        setStatus("Listening");
      };
      recognition.onerror = (event) => {
        setListening(false);
        setPanelError(event && event.error ? `Microphone error: ${event.error}` : "Microphone input failed.");
      };
      recognition.onresult = (event) => {
        let interim = "";
        let finalText = "";
        for (let index = event.resultIndex; index < event.results.length; index += 1) {
          const result = event.results[index];
          const text = result && result[0] ? result[0].transcript : "";
          if (result.isFinal) finalText += text;
          else interim += text;
        }
        const next = (finalText || interim).trim();
        if (next) setDraft(next);
        if (finalText.trim()) pendingTranscriptRef.current = finalText.trim();
      };
      recognition.onend = () => {
        setListening(false);
        const finalText = pendingTranscriptRef.current.trim();
        pendingTranscriptRef.current = "";
        if (finalText) sendPrompt(finalText, "Voice");
        else setStatus("Ready");
      };
      recognitionRef.current = recognition;
      recognition.start();
    }

    function startListening() {
      if (canRealtime) {
        startRealtimeVoice();
        return;
      }
      startDictation();
    }

    function stopListening() {
      if (realtimeRef.current) {
        stopRealtimeVoice();
        return;
      }
      if (recognitionRef.current) recognitionRef.current.stop();
    }

    const lastAssistant = [...messages].reverse().find((message) => message.role === "assistant" && message.content);
    const canSend = !!draft.trim() && !busy && !listening;

    return (
      <section className="voice-panel" role="dialog" aria-label="Appligator voice assistant" data-appligator-id="voice-assistant-panel">
        <header className="voice-head">
          <div className="voice-title">
            <span className={`voice-orb ${listening ? "listening" : busy ? "busy" : ""}`}><Icon name="mic" size={15} /></span>
            <div><strong>Appligator Voice</strong><span>{status}</span></div>
          </div>
          <div className="voice-head-actions">
            <button type="button" className="icon-btn" title="Restart conversation" aria-label="Restart voice assistant conversation" onClick={restartConversation} data-appligator-id="voice-assistant-restart"><Icon name="refresh" size={15} /></button>
            <button type="button" className="icon-btn" title="Close" aria-label="Close voice assistant" onClick={onClose}><Icon name="close" size={15} /></button>
          </div>
        </header>
        <div className="voice-thread">
          {messages.length === 0 && <div className="voice-empty">Ask about projects, customers, invoices, or current work.</div>}
          {messages.map((message) => (
            message.kind === "tool" ? (
              <div key={message.id} className={`voice-tool-message ${message.streaming ? "running" : "done"}`}>
                {message.streaming ? <span className="acw-spinner" /> : <Icon name="check" size={13} />}
                <span>Using <strong>{message.toolName || message.content}</strong></span>
              </div>
            ) : (
              <div key={message.id} className={`voice-message ${message.role === "user" ? "user" : "assistant"}`}>
                <span>{message.role === "user" ? "You" : "Appligator"}</span>
                <p>{message.content}</p>
              </div>
            )
          ))}
          {busy && <div className="voice-thinking"><span className="acw-spinner" />Working...</div>}
          <div ref={messagesEndRef} />
        </div>
        {error && <div className="voice-error"><Icon name="alert" size={13} />{error}</div>}
        <footer className="voice-controls">
          <div className="voice-composer">
            <textarea
              rows="2"
              value={draft}
              placeholder={listening ? "Listening..." : "Speak or type a message..."}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  sendPrompt(draft, "Text");
                }
              }}
              data-appligator-id="voice-assistant-composer"
            />
            <button type="button" className="voice-send" title="Send" aria-label="Send voice assistant message" disabled={!canSend} onClick={() => sendPrompt(draft, "Text")}><Icon name="arrowUp" size={16} /></button>
          </div>
          <div className="voice-actions">
            <button type="button" className={`voice-action ${listening ? "active" : ""}`} disabled={!canListen || busy} title={canListen ? (listening ? "Stop listening" : "Start listening") : "Speech recognition is not available in this browser"} onClick={listening ? stopListening : startListening} data-appligator-id="voice-assistant-mic">
              <Icon name={listening ? "stop" : "mic"} size={15} />{listening ? "Stop" : "Talk"}
            </button>
            <button type="button" className="voice-action" disabled={!canSpeak || !lastAssistant} title="Read last response aloud" onClick={() => lastAssistant && speak(lastAssistant.content)}>
              <Icon name="volume" size={15} />Replay
            </button>
            <label className="voice-toggle" title={canSpeak ? "Speak assistant replies" : "Speech playback is not available"}>
              <input type="checkbox" checked={autoSpeak} disabled={!canSpeak} onChange={(event) => setAutoSpeak(event.target.checked)} />
              <span>Speak replies</span>
            </label>
          </div>
          {!canListen && <div className="voice-support-note">Microphone dictation is not available in this browser.</div>}
        </footer>
      </section>
    );
  }

  Object.assign(window, {
    VoiceAssistantButton,
    VoiceAssistantPanel,
    voiceAssistantContext,
    DEFAULT_VOICE_ASSISTANT_INSTRUCTIONS,
  });
})();
